import { FormGroup, Label, Modal, SubmitButton, Textarea } from "@xola/ui-kit";
import clsx from "clsx";
import { requiredString } from "common";
import { Field, FieldProps } from "formik";
import React, { useEffect } from "react";
import { useMutation } from "react-query";
import { Except } from "type-fest";
import { Note } from "types";
import { object } from "yup";
import { api } from "../../../api/api";
import { useSeller } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { Form } from "../../Common/Form";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";

const validationSchema = object().shape({
    text: requiredString(),
});

interface CustomerNotePayload {
    text: string;
}

interface CustomerNoteModalProps {
    customerId: string;
    isOpen: boolean;
    onClose: () => void;
    note?: Note;
    heading?: string;
    purchaseId?: ID;
    eventId?: ID;
}

export const CreateCustomerNoteModal = ({
    customerId,
    onClose,
    isOpen,
    note,
    heading,
    purchaseId,
    eventId,
}: CustomerNoteModalProps) => {
    return (
        <Modal isOpen={isOpen} onClose={onClose}>
            <ModalContent
                customerId={customerId}
                note={note}
                heading={heading}
                purchaseId={purchaseId}
                eventId={eventId}
                onClose={onClose}
            />
        </Modal>
    );
};

const ModalContent = ({
    customerId,
    onClose,
    note,
    purchaseId,
    eventId,
    heading = "Customer",
}: Except<CustomerNoteModalProps, "isOpen">) => {
    const seller = useSeller();
    const invalidate = useInvalidate();

    const { mutateAsync: addCustomerNote, isLoading } = useMutation(async (payload: CustomerNotePayload) => {
        return api.post(`/sellers/${seller.id}/customers/${customerId}/notes`, payload);
    });

    const { mutateAsync: updateCustomerNote, isLoading: isCustomerNoteUpdateLoading } = useMutation(
        async (payload: CustomerNotePayload) => {
            if (eventId) {
                return api.put(`/events/${eventId}/notes/${note?.id}`, payload);
            }

            if (purchaseId) {
                return api.put(`/purchases/${purchaseId}/notes/${note?.id}`, payload);
            }

            return api.put(`/sellers/${seller.id}/customers/${customerId}/notes/${note?.id}`, payload);
        },
    );

    const handleSubmit = async (values: any) => {
        const payload = {
            text: values.text.trim(),
        };
        await (note?.id ? updateCustomerNote(payload) : addCustomerNote(payload));
        onClose();

        invalidate("sellers", seller.id);
        invalidate("events", eventId);
        invalidate("purchases", purchaseId);
        invalidate("purchaseItems", purchaseId);
    };

    useEffect(() => {
        const textField = document.querySelector("#text-field")!;

        if (textField) {
            (textField as HTMLInputElement)?.focus();
        }
    }, []);

    return (
        <XolaFormik
            validateOnChange
            initialValues={{ text: note?.text ?? "" }}
            validateOnBlur={false}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit }) => (
                <Form>
                    <Modal.Header>
                        {note?.id ? "Update" : "Add"} {heading} Note
                    </Modal.Header>

                    <Modal.Body>
                        <fieldset disabled={isLoading || isCustomerNoteUpdateLoading}>
                            <Field name="text">
                                {({ meta }: FieldProps) => (
                                    <FormGroup>
                                        <Label className={clsx(meta.error && "!text-danger")}>
                                            Note
                                            {meta.error ? <span className="ml-1 text-danger">{meta.error}</span> : null}
                                        </Label>
                                        <Field
                                            id="text-field"
                                            className="h-28"
                                            name="text"
                                            isError={!!meta.error}
                                            as={Textarea}
                                            placeholder="Text"
                                        />
                                    </FormGroup>
                                )}
                            </Field>
                        </fieldset>
                    </Modal.Body>

                    <Modal.Footer className="space-x-4">
                        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>

                        <SubmitButton isLoading={isLoading || isCustomerNoteUpdateLoading} onClick={handleSubmit}>
                            {note?.id ? "Update" : "Add"}
                        </SubmitButton>
                    </Modal.Footer>
                </Form>
            )}
        </XolaFormik>
    );
};
